import React, { useEffect } from 'react';
import { INPUT_TYPE, INPUT_VARIANTS } from '../../utils/constants/input';
import { IconContainer, InputButtonClear, InputContainer, InputErrorText, InputHelper, InputWrapper } from './styles';
import { handleChange, handleClickClear } from './utils';
import { ReactComponent as InputClear } from '../../assets/svg/input-clear.svg';
import clsx from 'clsx';

export type InputProps = {
  /**
   * Attribute to define the input value
   */
  value?: string;
  /**
   * Attribute to define if the field has any validation errors
   */
  error?: boolean;
  /**
   * Attribute to define help text at the bottom of the field
   */
  helper?: string;
  /**
   * Attribute to define the function when on change the input value
   */
  onChange?: (value: string) => void;
  /**
   * Attribute to define the input placeholder
   */
  placeholder?: string;
  /**
   * Attribute to define CSS styles
   */
  style?: React.CSSProperties;
  /**
   * Attribute to define the input type
   */
  type?: typeof INPUT_TYPE[number];
  /**
   * Attribute to define custom regex
   */
  customRegex?: RegExp;
  /**
   * Attribute to define custom regex
   */
  icon?: React.ReactNode;
  /**
   * Attribute to define the input style
   */
  variant?: typeof INPUT_VARIANTS[number];
  /**
   * Attribute to define onBlur Event
   */
  onBlur?: (event) => void;
  /**
   * Attribute for green outline when we enter any data
   */
  greenOutlineOnSuccess?: boolean;
  /**
   * Attribute for the error Text
   */
  errorText?: string;
};

export const defaultProps: InputProps = {
  value: '',
  error: false,
  type: 'text',
  variant: 'outline',
  greenOutlineOnSuccess: false,
  errorText: '',
};

const Input: React.FC<InputProps> = ({
  type,
  value,
  helper,
  onChange,
  error,
  customRegex,
  icon,
  variant,
  style,
  onBlur,
  greenOutlineOnSuccess,
  errorText,
  ...otherProps
}) => {
  useEffect(() => {
    if (!!value) {
      const initialEvent = { target: { value } } as any;
      handleChange({ event: initialEvent, onChange, type, customRegex });
    }
  }, []);

  return (
    <>
      <InputContainer
        data-testid="input-container"
        className={clsx({ error, success: value != '' && !error && greenOutlineOnSuccess })}
        variant={variant}
        style={style}
      >
        <InputWrapper
          data-testid="input"
          value={value}
          onChange={(event) => handleChange({ event, onChange, type, customRegex })}
          type={type}
          onBlur={(event) => onBlur(event)}
          {...otherProps}
        />
        {icon && <IconContainer> {icon} </IconContainer>}
        {value !== '' && !icon && (
          <InputButtonClear data-testid="input-button-clear" onClick={() => handleClickClear(onChange)}>
            <InputClear />
          </InputButtonClear>
        )}
      </InputContainer>
      {helper && (
        <InputHelper data-testid="input-helper" className={clsx({ error })} variant={variant}>
          {helper}
        </InputHelper>
      )}
      {errorText && error && <InputErrorText data-testid="input-errorText">{errorText}</InputErrorText>}
    </>
  );
};

Input.defaultProps = defaultProps;

export default Input;
